/**
 * PlanPublishedCard — the loop closed, made legible (Doc 26; SPEC-01 §6.4).
 *
 * When a cycle this viewer ranked in has a *published* cycle report, their
 * dashboard says so in one card: the ball is resolved, the provenance line
 * connects their session to the top, and the one CTA opens the published list.
 */

import { useNavigate } from 'react-router-dom';
import type { PublishedPlan } from '../hooks/usePublishedPlans';
import { C } from '../theme/participantTokens';

interface PlanPublishedCardProps {
  plan: PublishedPlan;
}

export function PlanPublishedCard({ plan }: PlanPublishedCardProps) {
  const navigate = useNavigate();
  const { report, cycleId } = plan;
  const por = report.planOfRecord;
  const sessionCount = por?.session_count ?? 0;
  const loopCount = por?.loop_count ?? 0;
  const open = () => navigate(`/prioritization/cycles/${cycleId}/report`);

  return (
    <div
      className="pp-anim pp-session-card"
      onClick={open}
      style={{
        background: C.surface,
        border: `1px solid ${C.rule}`,
        borderLeft: `2px solid ${C.green}`,
        borderRadius: 4,
        padding: '14px 16px',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'JetBrains Mono', fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textDim, marginBottom: 5, flexWrap: 'wrap' }}>
        <span style={{ color: 'rgba(58,125,86,0.9)' }}>Published priorities</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: C.green }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, flexShrink: 0 }} />
          Resolved
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>v{report.version}</span>
      </div>

      <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1rem', fontWeight: 400, color: C.textPri, lineHeight: 1.3 }}>
        The priorities you helped rank are now the plan.
      </div>

      <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.82rem', fontWeight: 300, color: C.textTer, marginTop: 6, lineHeight: 1.4 }}>
        {report.cycleName ? `${report.cycleName} — built` : 'Built'} from {sessionCount} session{sessionCount === 1 ? '' : 's'} across {loopCount} exchange{loopCount === 1 ? '' : 's'}
        {report.ratifiedBy ? `, ratified by ${report.ratifiedBy}` : ''}.
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 10, marginTop: 10, borderTop: `1px solid ${C.rule}` }}>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); open(); }}
          style={{
            height: 28,
            padding: '0 12px',
            background: 'none',
            border: '1px solid rgba(58,125,86,0.4)',
            borderRadius: 3,
            cursor: 'pointer',
            fontFamily: 'JetBrains Mono',
            fontSize: '0.58rem',
            letterSpacing: '0.1em',
            color: C.green,
          }}
        >
          See where things landed →
        </button>
      </div>
    </div>
  );
}
